import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import {
  CLUB_EDITABLE_LINK_FIELDS,
  CONSOLE_DEFAULT_RANGE,
  getApiErrorMessage,
  getClubDetailStats,
  getConsoleRangeLabel,
  normalizeExistingImages,
  safeExternalHref,
  CLAIM_LABEL,
} from "../../utils/consoleUtils";
import { ArrowLeft, Copy } from "lucide-react";
import { toast } from "sonner";
import { consoleApi } from "../../services/consoleApiService";

/**
 * Club detail panel: hero, completeness, run stats, details, actions.
 * Used when viewing a single club in the console clubs view.
 */
export default function ClubDetail({
  club,
  runs,
  cityId,
  meetingsRange = CONSOLE_DEFAULT_RANGE,
  detailCarouselPreviewUrl,
  setDetailCarouselPreviewUrl,
  onBack,
  onEdit,
  onInternalNotes,
  onAddRun,
  onUpcomingRuns,
  onRunsList,
  onMembers,
  onVerificationChange,
  onRemoveClubLink,
  onReplaceClubLink,
  onDeleteClub,
  clubDeleteBusy = false,
  onOrganizerClaimIssued,
}) {
  const [verifying, setVerifying] = useState(false);
  const [linkEditor, setLinkEditor] = useState(null);
  const [linkSavePending, setLinkSavePending] = useState(false);
  const [linkEditorError, setLinkEditorError] = useState(null);
  const [clubDeleteDialogOpen, setClubDeleteDialogOpen] = useState(false);
  const [clubDeleteTypedName, setClubDeleteTypedName] = useState("");
  const [claimIssueBusy, setClaimIssueBusy] = useState(false);
  const [lastIssuedClaimCode, setLastIssuedClaimCode] = useState("");

  useEffect(() => {
    setLinkEditor(null);
    setLinkEditorError(null);
    setClubDeleteDialogOpen(false);
    setClubDeleteTypedName("");
    setLastIssuedClaimCode("");
  }, [club?.id]);

  useEffect(() => {
    if ((club?.claim_status || "").toLowerCase() === "claimed") {
      setLastIssuedClaimCode("");
    }
  }, [club?.claim_status]);

  const handleIssueOrganizerClaimCode = async () => {
    if (!club?.id) return;
    if ((club.claim_status || "").toLowerCase() === "claimed") {
      toast.message("This club has already been claimed. You cannot issue a new claim code.");
      return;
    }
    setClaimIssueBusy(true);
    setLastIssuedClaimCode("");
    try {
      const data = await consoleApi.clubs.issueOrganizerClaimCode(club.id);
      if (data?.code) {
        setLastIssuedClaimCode(String(data.code));
      }
      toast.success("Claim code generated — copy it from the field under the action buttons (not stored after you leave).");
      if (typeof onOrganizerClaimIssued === "function") {
        onOrganizerClaimIssued();
      }
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setClaimIssueBusy(false);
    }
  };

  const handleCopyClaimCode = async () => {
    if (!lastIssuedClaimCode) return;
    try {
      await navigator.clipboard.writeText(lastIssuedClaimCode);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Could not copy — select the code and copy manually.");
    }
  };

  if (!club) return null;
  const c = club;
  const organizerClaimed = (c.claim_status || "").toLowerCase() === "claimed";

  const openLinkEditor = (field) => {
    if (!CLUB_EDITABLE_LINK_FIELDS[field]) return;
    const value = (c[field] && String(c[field])) || "";
    setLinkEditor({ field, value });
    setLinkEditorError(null);
  };

  const cancelLinkEditor = () => {
    setLinkEditor(null);
    setLinkEditorError(null);
  };

  const saveLinkEditor = async () => {
    if (!linkEditor || typeof onReplaceClubLink !== "function") return;
    const v = linkEditor.value.trim();
    const maxLen = CLUB_EDITABLE_LINK_FIELDS[linkEditor.field]?.maxLen ?? 500;
    if (!v) {
      setLinkEditorError("Enter a URL.");
      return;
    }
    if (v.length > maxLen) {
      setLinkEditorError(`Max ${maxLen} characters.`);
      return;
    }
    setLinkEditorError(null);
    setLinkSavePending(true);
    try {
      await onReplaceClubLink(c, linkEditor.field, v);
      setLinkEditor(null);
    } catch {
      /* error toast from parent */
    } finally {
      setLinkSavePending(false);
    }
  };
  const {
    pct,
    upcomingRunCount,
    completedRunCount,
    compFields,
    fillColor,
    missing,
    showMissing,
    missingLineColor,
  } = getClubDetailStats(c, runs, meetingsRange);

  const verifiedAtFormatted =
    c.verified_at &&
    (() => {
      const d = new Date(c.verified_at);
      return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    })();

  const profileInner =
    c.profile_picture ? (
      <img
        src={c.profile_picture}
        alt=""
        onError={(e) => {
          e.target.style.display = "none";
        }}
      />
    ) : (
      <div className="club-logo-placeholder">{c.name ? c.name.charAt(0) : ""}</div>
    );

  return (
    <div className="console-main-inner club-detail-page">
      <div className="club-detail-header">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="club-detail-back"
          onClick={onBack}
        >
          <ArrowLeft className="h-4 w-4 mr-1.5" aria-hidden />
          Back to Clubs
        </Button>
        <div className="club-detail-title-row">
          {typeof onEdit === "function" ? (
            <button
              type="button"
              className="club-detail-profile club-detail-profile--clickable"
              onClick={() => onEdit(c)}
              aria-label={`Edit club — ${(c.name ?? "").trim() || "club"} (profile picture and details)`}
            >
              {profileInner}
            </button>
          ) : (
            <div className="club-detail-profile">{profileInner}</div>
          )}
          <div className="club-detail-title-wrap">
            <div className="section-title">{c.name ?? ""}</div>
            {[c.city, c.region, c.country].filter(Boolean).length > 0 && (
              <div className="section-sub">{[c.city, c.region, c.country].filter(Boolean).join(" · ")}</div>
            )}
          </div>
          <div className="club-detail-actions-inline">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="tb-btn"
              disabled={claimIssueBusy || organizerClaimed}
              title={
                organizerClaimed
                  ? "This club has already been claimed. You cannot issue a new claim code."
                  : "One-time code for the organizer to enter under Claim your run club in the app"
              }
              onClick={handleIssueOrganizerClaimCode}
            >
              {organizerClaimed ? "Already claimed" : claimIssueBusy ? "Generating…" : "Generate claim code"}
            </Button>
            <Button variant="outline" size="sm" onClick={() => onEdit(c)} className="tb-btn">
              Edit Club
            </Button>
            {onInternalNotes && (
              <Button variant="outline" size="sm" onClick={() => onInternalNotes(c)} className="tb-btn">
                Internal notes
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              className="tb-btn text-[var(--console-accent)] border-[var(--console-accent)]"
              onClick={onAddRun}
            >
              + Add Run
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="tb-btn"
              onClick={() => onUpcomingRuns(c)}
              title={`View upcoming runs (${getConsoleRangeLabel(meetingsRange)})`}
            >
              Upcoming runs
            </Button>
          </div>
        </div>
        {lastIssuedClaimCode && !organizerClaimed ? (
          <div
            className="club-claim-code-callout"
            style={{
              marginTop: 12,
              padding: "12px 14px",
              borderRadius: 8,
              border: "1px solid var(--console-border)",
              background: "var(--console-surface-muted, #f4f4f5)",
            }}
          >
            <label className="text-xs font-semibold" style={{ color: "var(--console-muted)", display: "block", marginBottom: 6 }} htmlFor="club-organizer-claim-code-field">
              Organizer claim code
            </label>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
              <Input
                id="club-organizer-claim-code-field"
                readOnly
                value={lastIssuedClaimCode}
                onFocus={(e) => e.target.select()}
                className="font-mono text-sm flex-1 min-w-[12rem]"
                aria-label="Organizer claim code"
              />
              <Button type="button" size="sm" variant="secondary" className="shrink-0" onClick={handleCopyClaimCode}>
                <Copy className="h-4 w-4 mr-1.5" aria-hidden />
                Copy
              </Button>
            </div>
            <p className="text-xs m-0 mt-1.5" style={{ color: "var(--console-muted)", lineHeight: 1.4 }}>
              Shown only on this page until you go back or refresh. Send once via DM.
            </p>
          </div>
        ) : null}
        <div className="club-detail-hero-row">
          <div
            className="club-detail-hero"
            role={detailCarouselPreviewUrl ? "button" : undefined}
            tabIndex={detailCarouselPreviewUrl ? 0 : undefined}
            onClick={() => detailCarouselPreviewUrl && setDetailCarouselPreviewUrl(null)}
            onKeyDown={(e) =>
              detailCarouselPreviewUrl &&
              (e.key === "Enter" || e.key === " ") &&
              (e.preventDefault(), setDetailCarouselPreviewUrl(null))
            }
            title={detailCarouselPreviewUrl ? "Click to show main hero" : undefined}
          >
            <div className="club-detail-hero-img-wrap">
              {detailCarouselPreviewUrl ? (
                <img
                  src={detailCarouselPreviewUrl}
                  alt=""
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              ) : c.hero_image ? (
                <img
                  src={c.hero_image}
                  alt=""
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              ) : (
                <div className="club-hero-placeholder">No hero image</div>
              )}
            </div>
            {detailCarouselPreviewUrl && (
              <p className="text-xs" style={{ color: "var(--console-muted)", margin: 0 }}>
                Preview — click image above to show main hero
              </p>
            )}
          </div>
          {(detailCarouselPreviewUrl || c.hero_image) && (
            <div className="club-detail-hero-full" title="Full image">
              <img
                src={detailCarouselPreviewUrl || c.hero_image}
                alt=""
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            </div>
          )}
          {Array.isArray(c.clubcard_images) && c.clubcard_images.length > 0 && (
            <div className="club-detail-carousel">
              {normalizeExistingImages(c.clubcard_images).map((img, idx) => (
                <button
                  key={img.url ? `${img.url}-${idx}` : idx}
                  type="button"
                  className={`club-detail-carousel-item ${detailCarouselPreviewUrl === img.url ? "club-detail-carousel-item-active" : ""}`}
                  onClick={() => setDetailCarouselPreviewUrl(img.url)}
                  title="Preview in main image"
                >
                  <img
                    src={img.url}
                    alt=""
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </button>
              ))}
            </div>
          )}
        </div>
        {c.claim_status && (
          <span className={`claim-pill claim-${c.claim_status}`}>
            {CLAIM_LABEL[c.claim_status] ?? c.claim_status}
          </span>
        )}
        <div className="club-detail-section" style={{ marginTop: 12 }}>
          <div className="section-label">Organizer claim</div>
          {organizerClaimed ? (
            <p className="console-muted-text" style={{ fontSize: 13, marginBottom: 0, lineHeight: 1.45 }}>
              This club has already been claimed by an organizer. A new claim code cannot be issued.
            </p>
          ) : (
            <p className="console-muted-text" style={{ fontSize: 13, marginBottom: 0, lineHeight: 1.45 }}>
              Use <strong>Generate claim code</strong> above. The code appears in the highlighted field directly under the
              buttons. The organizer enters it in the Run.cards app: Log in → <strong>Claim your run club</strong>, then
              sign in with Gmail, Apple, or email and complete phone and location.
            </p>
          )}
        </div>
        <div
          className={`comp-dots ${pct === 100 ? "comp-dots--full" : ""}`}
          title="Dots: hero image · about · good-to-know · meet-up · run verified"
        >
          {compFields.map((f) => (
            <span key={f.key} className={`cdot ${f.ok ? "yes" : "no"}`} title={f.key} aria-hidden />
          ))}
        </div>
        <div
          className={`completeness-row ${pct === 100 ? "completeness-row--full" : ""}`}
          title="Club completeness. 70%+ = ready for app."
        >
          <div className="comp-track">
            <div className="comp-fill" style={{ width: `${pct}%`, background: fillColor }} />
          </div>
          <div className="comp-pct">{pct}%</div>
        </div>
        {showMissing && (
          <div style={{ fontSize: 12, color: missingLineColor, marginBottom: 12, lineHeight: 1.5 }}>
            <span style={{ fontWeight: 600, color: missingLineColor }}>For 100%:</span>{" "}
            {missing.map((m) => `- ${m}`).join(" · ")}
          </div>
        )}

        <div className="club-detail-section">
          <div className="section-label">Meetup spot</div>
          <div className="club-detail-meta">
            {c.home_base_address && (c.home_base_address || "").trim() ? (
              <>
                <p className="club-detail-meta-row">
                  <strong>Address:</strong> {c.home_base_address.trim()}
                </p>
                {c.home_base_description && (c.home_base_description || "").trim() && (
                  <p className="club-detail-desc">{c.home_base_description.trim()}</p>
                )}
              </>
            ) : (
              <p className="club-detail-meta-row" style={{ color: "var(--console-muted)", fontStyle: "italic" }}>No meetup spot set</p>
            )}
          </div>
        </div>

        <div className="club-detail-section">
          <div className="section-label">Run stats</div>
          <div className="club-detail-run-stats">
            <button
              type="button"
              className="club-runs-count"
              title={`Scheduled in ${getConsoleRangeLabel(meetingsRange)}`}
              onClick={() => onRunsList(c, "upcoming")}
            >
              {upcomingRunCount} Upcoming Run{upcomingRunCount !== 1 ? "s" : ""}
            </button>
            <button type="button" className="club-runs-count club-runs-count-completed" onClick={() => onRunsList(c, "completed")}>
              {completedRunCount} Completed Run{completedRunCount !== 1 ? "s" : ""}
            </button>
            <button
              type="button"
              className="club-detail-stat-pill club-detail-stat-pill-btn"
              onClick={() => onMembers(c)}
              title="View members"
            >
              {typeof c.members === "number" ? c.members : 0} member
              {(typeof c.members === "number" ? c.members : 0) !== 1 ? "s" : ""}
            </button>
          </div>
        </div>

        <div className="club-detail-section mt-5">
          <div className="section-label">Details</div>
          <div className="club-detail-meta">
            {(c.type || c.skill_level) && (
              <p className="club-detail-meta-row">
                {[
                  typeof c.type === "string" ? c.type : c.type?.value,
                  typeof c.skill_level === "string" ? c.skill_level : c.skill_level?.value,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            )}
            {c.description && <p className="club-detail-desc">{c.description}</p>}
            {Object.keys(CLUB_EDITABLE_LINK_FIELDS).some((k) => c[k]) && (
              <div className="club-detail-links-block">
                <p className="club-detail-meta-row">
                  <strong className="club-detail-links-heading">Links:</strong>
                </p>
                {Object.keys(CLUB_EDITABLE_LINK_FIELDS)
                  .filter((field) => c[field])
                  .map((field) => {
                    const cfg = CLUB_EDITABLE_LINK_FIELDS[field];
                    const raw = c[field];
                    const href = safeExternalHref(raw);
                    return (
                      <div key={field} className="club-detail-meta-row club-detail-links-item">
                        <span className="club-detail-link-with-action">
                          {href ? (
                            <a
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="club-detail-link"
                              title={raw}
                            >
                              <span className="club-detail-link-label">{cfg.label}: </span>
                              {raw}
                            </a>
                          ) : (
                            <span className="club-detail-link club-detail-link-text-only" title={String(raw)}>
                              <span className="club-detail-link-label">{cfg.label}: </span>
                              {raw}
                            </span>
                          )}
                          {typeof onReplaceClubLink === "function" && linkEditor?.field !== field && (
                            <button
                              type="button"
                              className="club-detail-link-remove"
                              onClick={() => openLinkEditor(field)}
                              aria-label={`Replace ${cfg.label} URL`}
                            >
                              Replace
                            </button>
                          )}
                          {typeof onRemoveClubLink === "function" && linkEditor?.field !== field && (
                            <button
                              type="button"
                              className="club-detail-link-remove"
                              onClick={() => onRemoveClubLink(c, field)}
                              aria-label={`Remove ${cfg.label} URL`}
                            >
                              Remove
                            </button>
                          )}
                        </span>
                      </div>
                    );
                  })}
                {linkEditor && CLUB_EDITABLE_LINK_FIELDS[linkEditor.field] && (
                  <div className="club-detail-link-editor">
                    <label className="club-detail-link-editor-label" htmlFor={`club-link-edit-${linkEditor.field}`}>
                      {CLUB_EDITABLE_LINK_FIELDS[linkEditor.field].label} URL
                    </label>
                    <input
                      id={`club-link-edit-${linkEditor.field}`}
                      type="text"
                      inputMode="url"
                      className="club-detail-link-editor-input"
                      value={linkEditor.value}
                      onChange={(e) => setLinkEditor((prev) => (prev ? { ...prev, value: e.target.value } : prev))}
                      disabled={linkSavePending}
                      maxLength={CLUB_EDITABLE_LINK_FIELDS[linkEditor.field].maxLen}
                      placeholder="https://…"
                      autoComplete="off"
                    />
                    {linkEditorError && (
                      <p className="club-detail-link-editor-error" role="alert">
                        {linkEditorError}
                      </p>
                    )}
                    <div className="club-detail-link-editor-actions">
                      <Button type="button" size="sm" onClick={saveLinkEditor} disabled={linkSavePending}>
                        {linkSavePending ? "Saving…" : "Save URL"}
                      </Button>
                      <button
                        type="button"
                        className="club-detail-link-remove"
                        onClick={cancelLinkEditor}
                        disabled={linkSavePending}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {(c.contact_email || c.contact_phone) && (
              <p className="club-detail-meta-row">
                <strong>Contact:</strong> {[c.contact_email, c.contact_phone].filter(Boolean).join(" · ")}
              </p>
            )}
            {c.organizer_name && (
              <p className="club-detail-meta-row club-detail-organizer-row">
                {c.organizer_profile_picture && (
                  <img
                    src={c.organizer_profile_picture}
                    alt=""
                    className="club-detail-organizer-avatar"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                )}
                <strong>Organizer:</strong> {c.organizer_name}
              </p>
            )}
            {(c.state || c.country || c.zip_code) && (
              <p className="club-detail-meta-row">
                <strong>Location:</strong> {[c.state, c.country, c.zip_code].filter(Boolean).join(" · ")}
              </p>
            )}
            {Array.isArray(c.paces) && c.paces.length > 0 && (
              <p className="club-detail-meta-row">
                <strong>Paces:</strong> {c.paces.filter(Boolean).join(", ")}
              </p>
            )}
            <p className="club-detail-meta-row">
              <strong>Recurring runs vary:</strong> {c.recurring_runs_vary ? "Yes" : "No"}
              {" · "}
              <strong>Public:</strong> {c.is_public ? "Yes" : "No"}
            </p>
            {typeof c.rating === "number" && (c.total_ratings ?? 0) > 0 && (
              <p className="club-detail-meta-row">
                <strong>Rating:</strong> {c.rating.toFixed(1)} ({(c.total_ratings ?? 0)} rating
                {(c.total_ratings ?? 0) !== 1 ? "s" : ""})
              </p>
            )}
            {c.created_at && (
              <p className="club-detail-meta-row club-detail-meta-muted">
                <strong>Created:</strong>{" "}
                {new Date(c.created_at).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </p>
            )}
            <p className="club-detail-meta-row">
              <strong>Admin verified:</strong>{" "}
              {c.admin_verified ? "Yes" : "No"}
              {c.admin_verified && verifiedAtFormatted && (
                <span className="club-detail-meta-muted" style={{ marginLeft: 6 }}>
                  ({verifiedAtFormatted})
                </span>
              )}
              {typeof onVerificationChange === "function" && c.id && (
                <>
                  {" "}
                  {c.admin_verified ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="ml-2"
                      disabled={verifying}
                      onClick={() => {
                        setVerifying(true);
                        Promise.resolve(onVerificationChange(c.id, false)).finally(() => setVerifying(false));
                      }}
                    >
                      {verifying ? "Updating…" : "Unverify"}
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="ml-2"
                      disabled={verifying}
                      onClick={() => {
                        setVerifying(true);
                        Promise.resolve(onVerificationChange(c.id, true)).finally(() => setVerifying(false));
                      }}
                    >
                      {verifying ? "Updating…" : "Mark as verified"}
                    </Button>
                  )}
                </>
              )}
            </p>
            {c.internal_notes && (
              <p className="club-detail-desc club-detail-internal-notes">
                <strong>Internal notes:</strong> {c.internal_notes}
              </p>
            )}
            {Array.isArray(c.club_notes) && c.club_notes.filter(Boolean).length > 0 && (
              <p className="club-detail-desc club-detail-notes">{c.club_notes.filter(Boolean).join(" ")}</p>
            )}
          </div>
        </div>

        {typeof onDeleteClub === "function" && c.id ? (
          <>
            <div className="club-detail-section" style={{ borderTop: "1px solid var(--console-border)", paddingTop: 16 }}>
              <div className="section-label" style={{ color: "var(--console-red, #b91c1c)" }}>
                Danger zone
              </div>
              <p className="text-sm mb-3" style={{ color: "var(--console-muted)", maxWidth: 560, lineHeight: 1.5 }}>
                Permanently delete this club, all of its scheduled runs, registrations, member connections to this club,
                club chat, and the run club organizer user account tied to this club. This cannot be undone.
              </p>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                disabled={clubDeleteBusy}
                onClick={() => {
                  setClubDeleteTypedName("");
                  setClubDeleteDialogOpen(true);
                }}
              >
                Delete club…
              </Button>
            </div>

            <AlertDialog
              open={clubDeleteDialogOpen}
              onOpenChange={(open) => {
                setClubDeleteDialogOpen(open);
                if (!open) setClubDeleteTypedName("");
              }}
            >
              <AlertDialogContent className="console-page bg-[var(--console-surface)] border-[var(--console-border)] text-[var(--console-ink)]">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-[var(--console-ink)]">Delete this club?</AlertDialogTitle>
                  <AlertDialogDescription className="text-[var(--console-muted)] space-y-3">
                    <span className="block">
                      Type{" "}
                      <strong className="text-[var(--console-ink)]">
                        {(c.name ?? "").trim() || c.id}
                      </strong>{" "}
                      to confirm {(c.name ?? "").trim() ? "(club name)" : "(club id)"}.
                    </span>
                    <Input
                      value={clubDeleteTypedName}
                      onChange={(e) => setClubDeleteTypedName(e.target.value)}
                      placeholder="Club name"
                      className="bg-[var(--console-surface)] border-[var(--console-border)]"
                      autoComplete="off"
                      aria-label="Type club name to confirm deletion"
                    />
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="tb-btn" disabled={clubDeleteBusy}>
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    className="tb-btn bg-[var(--console-red)] hover:opacity-90 text-white"
                    disabled={(() => {
                      const expected = ((c.name ?? "").trim() || (c.id ?? "").trim());
                      return (
                        clubDeleteBusy ||
                        !expected ||
                        (clubDeleteTypedName ?? "").trim() !== expected
                      );
                    })()}
                    onClick={async (e) => {
                      e.preventDefault();
                      if (clubDeleteBusy) return;
                      try {
                        await onDeleteClub(c);
                        setClubDeleteDialogOpen(false);
                        setClubDeleteTypedName("");
                      } catch {
                        /* parent shows toast */
                      }
                    }}
                  >
                    {clubDeleteBusy ? "Deleting…" : "Delete club permanently"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        ) : null}
      </div>
    </div>
  );
}
